import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useJsBridgeContext } from 'src/components/jsbridge/JsBridgeContext'
import { isPaymentAvailable } from 'src/components/Membership/MembershipPaymentUnavailableNotice'
import { useMyInfo } from 'src/services/apollo/auth'
import {
  type Membership,
  type UserSubscriptionDetailFragment,
} from 'src/services/apollo/generated/graphql'
import { Interval } from 'src/services/apollo/generated/graphql'
import { useMyMembership } from 'src/services/apollo/membership'
import { useSubscriptionInfo } from 'src/services/helpers/membership'
import { UserSubscriptionStatus } from 'src/utils/user'
import type {
  MembershipCardState,
  MembershipCardStateAction,
} from './stateMachine'
import {
  type CardButtonProps,
  getMembershipCardState,
  type MembershipCardStateInfo,
} from './stateMachine'

// Pending subscription statuses
const pendingSubscriptionStatus = [
  UserSubscriptionStatus.pending,
  UserSubscriptionStatus.inactive,
] as string[]

interface MembershipCardStateProps {
  planTier: number
  planId: string
  interval: Interval
  enableCancel?: boolean
  membership?: Membership
  subscription?: UserSubscriptionDetailFragment
}

export interface CardStateResult extends CardButtonProps {
  state: MembershipCardState
  stateAction: MembershipCardStateAction
  stateInfo: MembershipCardStateInfo | undefined
}

export function useMembershipCardState(
  props: MembershipCardStateProps,
): CardStateResult {
  const { t } = useTranslation(['membership'])
  const subscriptionInfo = useSubscriptionInfo()
  // The accurate card state can only be determined after all necessary data is
  // resolved.
  const { loading: loadingSubscription } = useMyMembership()
  const { data: user, loading: loadingMe } = useMyInfo()

  const {
    planTier,
    interval,
    membership,
    subscription,
    enableCancel = false,
  } = props

  // Technically the readiness of this should also be considered while
  // determining the card state. But it involves more work to properly achieve,
  // and it should be rare to hit that case. So we skip it for now.
  const jsb = useJsBridgeContext()

  // Build state context with simplified state machine
  const cardStateInfo = useMemo((): MembershipCardStateInfo | undefined => {
    // The proper state cannot be determined while all necessary data is not ready
    if (loadingMe || loadingSubscription) {
      return undefined
    }

    const currentTier = membership?.tier ?? null

    // Interval information for upgrade/downgrade restrictions
    const isSameInterval =
      !membership ||
      !subscription ||
      interval === (subscription.interval ?? Interval.Month)

    return {
      // User info
      isLoggedIn: !!user,

      // Membership info
      currentTier,
      targetTier: planTier,

      // Subscription info
      hasActiveSubscription: subscriptionInfo.isActive,
      subscriptionStatus: subscription?.status,
      isCanceled: Boolean(subscription?.cancelAtPeriodEnd),

      // Payment info
      paymentProvider: subscription?.provider,
      isPaymentAvailable: isPaymentAvailable(),
      isUpgradeDowngradeSupported:
        subscriptionInfo.isUpgradeDowngradeSupportedProvider,

      // Business rules
      enableCancel,

      subscriptionEndDate: subscription?.endAt,

      // Interval information for upgrade/downgrade restrictions
      currentInterval: subscription?.interval,
      targetInterval: interval,
      isSameInterval,

      // JsBridge platform
      mobilePlatform: jsb.platform,
    }
  }, [
    loadingMe,
    loadingSubscription,
    user,
    planTier,
    interval,
    membership,
    subscription,
    subscriptionInfo,
    enableCancel,
    jsb.platform,
  ])

  // Get complete membership card state using the new SSOT function
  const membershipCardState = useMemo(
    () => getMembershipCardState(cardStateInfo, t),
    [cardStateInfo, t],
  )

  return {
    state: membershipCardState.state,
    stateAction: membershipCardState.uiProps.action,
    stateInfo: cardStateInfo,
    ...membershipCardState.uiProps,
  }
}
